import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { isFirebaseConfigured } from '@/firebase';
import {
  fetchHistoryPage,
  subscribeToQueue,
  subscribeToRecent,
} from '@/services/downloadService';
import { useAuthStore } from '@/store/authStore';
import { useDownloadStore } from '@/store/downloadStore';
import type { DownloadRecord } from '@/types';

/** Keep the queue store synced with Firestore for signed-in users. */
export function useQueueSync(): void {
  const uid = useAuthStore((s) => s.user?.uid);
  const syncQueue = useDownloadStore((s) => s.syncQueue);

  useEffect(() => {
    if (!uid || !isFirebaseConfigured) return;
    const unsubscribe = subscribeToQueue(uid, syncQueue);
    return () => {
      unsubscribe();
      syncQueue([]);
    };
  }, [uid, syncQueue]);
}

/** Recent completed downloads — live for users, local for guests. */
export function useRecentDownloads(count = 6): { records: DownloadRecord[]; isGuest: boolean } {
  const uid = useAuthStore((s) => s.user?.uid);
  const guestHistory = useDownloadStore((s) => s.guestHistory);
  const [records, setRecords] = useState<DownloadRecord[]>([]);

  useEffect(() => {
    if (!uid || !isFirebaseConfigured) {
      setRecords([]);
      return;
    }
    return subscribeToRecent(uid, setRecords, count);
  }, [uid, count]);

  if (!uid) {
    return { records: guestHistory.filter((r) => r.status === 'completed').slice(0, count), isGuest: true };
  }
  return { records, isGuest: false };
}

/** Paginated full history for signed-in users. */
export function useHistoryQuery() {
  const uid = useAuthStore((s) => s.user?.uid);

  return useInfiniteQuery({
    queryKey: ['history', uid],
    enabled: Boolean(uid) && isFirebaseConfigured,
    initialPageParam: null as QueryDocumentSnapshot<DocumentData> | null,
    queryFn: ({ pageParam }) => fetchHistoryPage(uid as string, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}
